<script>
import HighlightedSpan from "./ProductCardSelectableTextHighlighted";

/**
 * This components manages all the logic to select and higlight text.
 * It has a very deep connection with the selection object of the 'ner'
 * module of the store, it sets this object when there is a selection.
 * It renders the text enclosed with <span> inside a <div>, so the component
 * should look like <div><span>some text</span><span>other text</span></div>.
 */
export default {
  props: {
    /**
     * The product object with all its data.
     * To see all the things that a product could have please visit the
     * ner module of the store.
     */
    product: {
      type: Object,
      required: true
    },
    /**
     * Indicates which raw data this component will show.
     * The components does `this.product[this.data]` to get the raw string.
     */
    data: {
      type: String,
      required: true
    },
    /**
     * The id of the data that this component shows.
     */
    dataId: {
      type: Number,
      required: true
    }
  },
  computed: {
    taggedFeatures() {
      return this.product.taggedFeatures.filter(f => f.dataId === this.dataId);
    },
    /**
     * Return the text to show in this component.
     */
    text() {
      return this.product[this.data];
    }
  },
  methods: {
    /**
     * Enclose the spans with the div that handles the selection.
     */
    encloseSpans(spans) {
      return <div vOn:click={this.handleSelection}>{spans}</div>;
    },
    /**
     * Set the selection in the store.
     */
    handleSelection(event) {
      const selection = document.getSelection();
      const value = selection.toString();
      const position = { x: event.clientX, y: event.clientY };
      const dataId = this.dataId;
      const product = this.product;

      /* Raise an error when selecting an already selected text */
      const isDiv = event.target.tagName === "DIV";
      const isHighlighted = event.target.className.includes("highlighted");
      if (value && (isDiv || isHighlighted)) {
        return this.$notify.error("No puede asignar un tag sobre otros tags.");
      }

      /* The offset is how many characters from the start of this span are
      before the selection starts */
      let offset = selection.anchorOffset;
      /* Get all the spans of this component */
      const spans = [...event.target.parentNode.childNodes];
      /* Add to the offset all the characters of the previous spans */
      const targetIndex = spans.findIndex(span => span === event.target);
      for (let i = 0; i < targetIndex; i++) {
        offset += spans[i].textContent.length;
      }

      if (value) {
        const payload = { dataId, offset, product, position, value };
        this.$emit("selection", payload);
        this.$store.commit("ner/setSelection", payload);
      }
    }
  },
  render() {
    /* If there are no features in this product's section return the simple text */
    if (!this.taggedFeatures.length) {
      return this.encloseSpans(<span>{this.text}</span>);
    }

    /* Order the features by the offset */
    const taggedFeatures = [...this.taggedFeatures].sort((a, b) =>
      a.offset > b.offset ? 1 : -1
    );

    /**
     * We are going to get chunks of the text that each one
     * starts where each feature starts.
     * It's like splitting the text just in the index of the offset,
     * so we'll have `features.length + 1` chunks.
     */

    /* The first chunk has no feature and could be possibly empty: */
    let chunks = [this.text.slice(0, taggedFeatures[0].offset)];

    /* Now, all the others chunks starts with the string of a feature: */
    chunks.push(
      ...taggedFeatures.map((feature, index) => {
        let next;
        if (index + 1 < taggedFeatures.length) {
          next = taggedFeatures[index + 1].offset;
        }
        return this.text.slice(feature.offset, next);
      })
    );

    /* We could iterate over the chunks and mark each string with its correspondent tag */
    const spans = chunks.reduce((chunks, chunk, index) => {
      if (index === 0) {
        /* The first element has no feature */
        return chunk ? [<span>{chunk}</span>] : [];
      }

      const taggedFeature = taggedFeatures[index - 1];
      const notTaggedText = chunk.slice(taggedFeature.value.length);
      return [
        ...chunks,
        <HighlightedSpan
          product={this.product}
          taggedFeature={taggedFeature}
        />,
        <span>{notTaggedText}</span>
      ];
    }, []);

    return this.encloseSpans(spans);
  }
};
</script>
